import React from 'react';
import { motion } from 'framer-motion';
import SimProfileAvatar from './SimProfileAvatar';
import { User, Upload, ImageSquare } from '@phosphor-icons/react';

/**
 * SIM CUSTOMIZATION SHOWCASE
 * Demonstrates the SIM Profile Avatar customization feature
 */

const SimCustomizationShowcase = () => {
  const exampleSIMs = [
    { id: 'sim_001', name: 'Agent Rex', role: 'Greeter' },
    { id: 'sim_002', name: 'Scholar Xavier', role: 'Teacher' },
    { id: 'sim_003', name: 'Aria', role: 'Creative Generator' }
  ];

  return (
    <section className="w-full py-16 px-6 bg-gradient-to-b from-black to-[#0A0A0F]">
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <h2 className="text-4xl md:text-5xl font-black text-white mb-4">
            Customize Your SIM Profiles
          </h2>
          <p className="text-lg text-gray-400 max-w-3xl mx-auto">
            Each SIM starts with our signature cybernetic avatar. Upload custom faces to personalize your Federation experience.
          </p>
        </motion.div>

        {/* Feature Cards */}
        <div className="grid md:grid-cols-3 gap-8 mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="p-6 glass-card border border-white/10 rounded-xl text-center"
          >
            <ImageSquare size={48} weight="duotone" className="text-[#00F0FF] mx-auto mb-4" />
            <h3 className="text-xl font-bold text-white mb-2">Default Avatar</h3>
            <p className="text-sm text-gray-400">
              All SIMs start with our blue cybernetic head — a universal default for the Federation.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="p-6 glass-card border border-white/10 rounded-xl text-center"
          >
            <Upload size={48} weight="duotone" className="text-[#00FFA3] mx-auto mb-4" />
            <h3 className="text-xl font-bold text-white mb-2">Upload Custom Face</h3>
            <p className="text-sm text-gray-400">
              Personalize any SIM with your own image. Supports JPG, PNG, WEBP up to 5MB.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3 }}
            className="p-6 glass-card border border-white/10 rounded-xl text-center"
          >
            <User size={48} weight="duotone" className="text-[#FFB800] mx-auto mb-4" />
            <h3 className="text-xl font-bold text-white mb-2">Your SIM, Your Way</h3>
            <p className="text-sm text-gray-400">
              Reset to default anytime. Your custom faces persist across sessions.
            </p>
          </motion.div>
        </div>

        {/* Live Demo */}
        <div className="p-8 glass-surface border-2 border-[#00F0FF]/30 rounded-2xl">
          <h3 className="text-2xl font-bold text-white mb-6 text-center">
            Try It Now — Customize Sample SIMs
          </h3>
          
          <div className="grid sm:grid-cols-3 gap-8 justify-items-center">
            {exampleSIMs.map((sim, idx) => (
              <motion.div
                key={sim.id}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="text-center"
              >
                <SimProfileAvatar
                  simId={sim.id}
                  size="large"
                  showUploadButton={true}
                />
                <div className="mt-4">
                  <p className="text-white font-bold">{sim.name}</p>
                  <p className="text-xs text-gray-400">{sim.role}</p>
                </div>
              </motion.div>
            ))}
          </div>

          <div className="mt-8 p-4 bg-[#00F0FF]/10 border border-[#00F0FF]/30 rounded-lg">
            <p className="text-sm text-gray-300 text-center">
              <span className="text-[#00F0FF] font-bold">Hover</span> over any avatar to reveal the upload button. 
              Your changes are <span className="text-[#00FFA3] font-bold">saved automatically</span> and persist across page reloads.
            </p>
          </div>
        </div>

        {/* Technical Specs */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="mt-12 grid md:grid-cols-2 gap-6"
        >
          <div className="p-6 bg-black/60 border border-gray-700 rounded-xl">
            <h4 className="text-lg font-bold text-white mb-3">Upload Requirements</h4>
            <ul className="space-y-2 text-sm text-gray-400">
              <li className="flex items-start gap-2">
                <span className="text-[#00FFA3]">✓</span>
                <span>Supported formats: JPG, JPEG, PNG, WEBP</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-[#00FFA3]">✓</span>
                <span>Maximum file size: 5MB</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-[#00FFA3]">✓</span>
                <span>Preview before confirming</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-[#00FFA3]">✓</span>
                <span>Reset to default anytime</span>
              </li>
            </ul>
          </div>

          <div className="p-6 bg-black/60 border border-gray-700 rounded-xl">
            <h4 className="text-lg font-bold text-white mb-3">Features</h4>
            <ul className="space-y-2 text-sm text-gray-400">
              <li className="flex items-start gap-2">
                <span className="text-[#00F0FF]">◆</span>
                <span>LocalStorage persistence — no server upload needed</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-[#00F0FF]">◆</span>
                <span>Hover-to-reveal upload interface</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-[#00F0FF]">◆</span>
                <span>Custom images get cyan glow indicator</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-[#00F0FF]">◆</span>
                <span>Smooth animations and transitions</span>
              </li>
            </ul>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default SimCustomizationShowcase;
